import React from "react"

export const ComplianceLabelTooltip = ({course}) => {
  const labelData = course.labelData

  const humanizedTerminology = () => {
    switch(labelData.terminology)
    {
    case 'crs':
      return window.I18n.t('course.process_crs_warnings.crd')
    case 'scorecentre':
      return window.I18n.t('course.process_crs_warnings.score_centre')
    }
  }

  const humanizedTerminologyUsga = () => {
    switch(labelData.terminology)
    {
    case 'crs':
      return window.I18n.t('course.process_crs_warnings.usga_crd')
    case 'scorecentre':
      return window.I18n.t('course.process_crs_warnings.score_centre')
    }
  }

  const getIdentificationString = () => {
    switch(labelData.terminology)
    {
    case 'crs':
      return `${window.I18n.t('courses.bootstrap_course_table_data.usga_crd_id')}: ${course.sourceId}`
    case 'scorecentre':
      return `${window.I18n.t('courses.bootstrap_course_table_data.score_centre_id')}: ${course.sourceId}`
    case 'whsuk':
      return `${window.I18n.t('courses.bootstrap_course_table_data.whs_id')}: ${course.sourceId}`
    }
  }

  const getImportDate = () => {
    const date = new Date(course.createdAt)
    return `${window.I18n.t('courses.bootstrap_course_table_data.import_date')}: ${date.toUTCString()}`
  }

  const getLastChangedDate = () => {
    const date = new Date(course.updatedAt)
    return `${window.I18n.t('courses.bootstrap_course_table_data.last_changed')}: ${date.toUTCString()}`
  }

  const getComplianceStatus = () => {
    if (labelData.compliance_not_checked) {
      return window.I18n.t('course.process_crs_warnings.compliance_not_checked_plain')
    }
    return window.I18n.t('course.process_crs_warnings.compliance_checked')
  }

  const getMissingCourseWarning = () => {
    if (!labelData.missing_course) return null

    return (
      <p>• {window.I18n.t('course.process_crs_warnings.course_not_present_in', { name: humanizedTerminology() })}.</p>
    )
  }

  const getMissingTeesWarning = () => {
    const missingTees = labelData.missing_tees || []
    if (missingTees.length === 0) return null

    return (
      <p>
        • {window.I18n.t('course.process_crs_warnings.tees_not_imported_in_tm')}:{' '}
        {missingTees.map((teeName, idx) => (
          <span key={idx}>
            <strong>{teeName}</strong>
            {idx < missingTees.length - 1 ? ', ' : ''}
          </span>
        ))}
      </p>
    )
  }

  const getDifferentTeesWarning = () => {
    const differentTees = labelData.different_tees || []
    if (differentTees.length === 0) return null

    return (
      <p>
        • {window.I18n.t('course.process_crs_warnings.tees_not_valid_or_not_imported_in_tm')}:{' '}
        {differentTees.map((teeName, idx) => (
          <span key={idx}>
            <strong>{teeName}</strong>
            {idx < differentTees.length - 1 ? ', ' : ''}
          </span>
        ))}
      </p>
    )
  }

  const getInconsistentTeesWarnings = () => {
    const inconsistentTees = labelData.inconsistent_tees_warnings
    if (!inconsistentTees || Object.keys(inconsistentTees).length === 0) return null

    return Object.entries(inconsistentTees).map(([teeName, warnings]) => (
      <p key={teeName}>
        • <strong>{teeName}</strong>{' '}
        {window.I18n.t('course.process_crs_warnings.has_different_data')}:{' '}
        {labelData.terminology === 'scorecentre' ? (
          <ul>
            {warnings.map((w, idx) => <li key={idx} dangerouslySetInnerHTML={{ __html: w }} />)}
          </ul>
        ) : (
          warnings.join(', ')
        )}
      </p>
    ))
  }

  const getTeesNotPresentWarning = () => {
    const notPresentTees = labelData.not_present_tees || []
    if (notPresentTees.length === 0) return null

    return (
      <>
        <p>
          • {window.I18n.t('course.process_crs_warnings.tees_not_present_in', { name: humanizedTerminology() })}:{' '}
          {notPresentTees.map((teeName, idx) => (
            <span key={idx}>
              <strong>{teeName}</strong>
              {idx < notPresentTees.length - 1 ? ', ' : ''}
            </span>
          ))}
        </p>
        {labelData.show_manual_tees_ignored && (
          <p>• {window.I18n.t('course.process_crs_warnings.manual_tees_ignored')}</p>
        )}
      </>
    )
  }

  const getActionText = () => {
    const action = labelData.action
    if (!action) return null

    return (
      <>
        {action.stroke_index_par && (
          <p><br/>{window.I18n.t('course.process_crs_warnings.stroke_index_par')}</p>
        )}
        {action.import_course_again && (
          <p><br/>{window.I18n.t('course.process_crs_warnings.import_course_again', { name: humanizedTerminologyUsga() })}</p>
        )}
        {action.import_course_again_missing_tees && (
          <p><br/>{window.I18n.t('course.process_crs_warnings.import_course_again_missing_tees', { name: humanizedTerminologyUsga() })}</p>
        )}
      </>
    )
  }

  const hasCrdWarnings = () => {
    return (labelData.missing_tees && labelData.missing_tees.length > 0) ||
      (labelData.different_tees && labelData.different_tees.length > 0) ||
      (labelData.inconsistent_tees_warnings && Object.keys(labelData.inconsistent_tees_warnings).length > 0)
  }

  const hasTmData = () => {
    return labelData.missing_course || (labelData.not_present_tees || []).length > 0
  }

  return(
    <div className="courses-tooltip-container">
      {getIdentificationString()}
      <br/>
      {getImportDate()}
      <br/>
      <div className="add-margin-bottom-5">
        {getLastChangedDate()}
      </div>
      <br/>
      {getComplianceStatus()}

      {hasCrdWarnings() && (
        <>
          <br/>
          <p>
            {window.I18n.t('course.process_crs_warnings.course_directory_data', {
              name: labelData.terminology === 'crs'
                ? window.I18n.t('course.process_crs_warnings.usga_crd_long')
                : window.I18n.t('course.process_crs_warnings.score_centre')
            })}:
          </p>
          {getMissingTeesWarning()}
          {getDifferentTeesWarning()}
          {getInconsistentTeesWarnings()}
        </>
      )}

      {hasTmData() && (
        <>
          <p>{window.I18n.t('course.process_crs_warnings.tm_data')}:</p>
          {getMissingCourseWarning()}
          {getTeesNotPresentWarning()}
        </>
      )}

      {getActionText()}
    </div>
  )
}
